// page-meeting-invitees.ts — Onglet « Invités » de la fiche réunion.
//
// Deux responsabilités, extraites de `page-meeting.ts` (à son plafond de
// taille, cliquet CI) :
//
//  1. Le rendu des champs Animateurs / Participants (pastilles RACI).
//  2. La MISE EN ÉVIDENCE issue de la proposition de participants (août 2026) :
//     après un clic sur « Proposer une liste de participants », le calcul de
//     pertinence sert à relire la liste déjà saisie au lieu de rester un simple
//     panneau à côté.
//     - chaque participant voit son trigramme souligné de la couleur de son
//       score relatif (rouge = peu de raisons d'être là ; un participant absent
//       du calcul vaut 0 pt, donc rouge) ;
//     - dans « Autres participants suggérés », le trigramme d'un utilisateur qui
//       n'est pas participant est souligné de la couleur de sa propre barre de
//       score relatif — c'est un oubli probable, et le soulignement dit la même
//       chose que la barre d'à côté. Une barre rouge n'est PAS soulignée :
//       l'absence de quelqu'un sans raison identifiée d'être là est normale.
//       Le soulignement disparaît dès qu'on l'ajoute aux participants.
//
// Les deux listes se mettent à jour en direct : un observateur suit les
// pastilles du champ Participants (ajout via le picker, la proposition, ou le
// « × » d'une pastille).

import { relativeScoreColor, suggestionUnderlineColor } from '../domain/invitee-highlight.js';
import {
  _addRaciChip,
  _getRaciIds,
  _removeRaciChip,
  showRaciPicker,
} from './component-action-modal/raci-picker.js';
import {
  fetchInvitationProposal,
  renderProposalPanel,
  type InvitationResult,
  type PilotUser,
} from './invitation-proposal.js';
import type { MeetingPageState } from '../domain/meeting-form.js';

/** Champs RACI de la réunion (préfixe des ids DOM : `chips-<field>`). */
const PARTICIPANTS_FIELD = 'mp-participants';
const ANIMATORS_FIELD = 'mp-animators';

/** Cas d'apparition de la mascotte déclenché à l'ouverture d'une fiche réunion
 * (catalogue côté serveur : `services/mascot_seed.TRIGGER_CASES`). */
export const MASCOT_CASE_MEETING_OPEN = 'meeting_update_open';

const UNDERLINE = '2px';

interface ChipUser {
  id: number;
  trigram: string;
  name: string;
  is_team: boolean;
}

// ─── Rendu des champs RACI ──────────────────────────────────────────────────

function chipsHtml(
  users: ReadonlyArray<ChipUser>,
  field: string,
  ids: ReadonlyArray<number>,
): string {
  const sel = ids
    .map((idv) => users.find((u) => u.id === idv))
    .filter((u): u is ChipUser => Boolean(u));
  if (!sel.length)
    return `<span class="raci-empty" style="color:var(--text3);font-size:11px">—</span>`;
  // Le libellé est isolé dans un `<span>` : c'est LUI qu'on souligne, pour ne
  // pas décorer aussi le « × » de suppression.
  return sel
    .map(
      (u) => `<span class="raci-chip" data-uid="${u.id}" data-field="${field}"
        title="${esc(u.name)}"
        style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
               font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
        <span class="raci-chip-label">${esc(userLabel(u, true))}</span>
        <button data-remove-raci="${u.id}" data-field="${field}"
          style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
      </span>`,
    )
    .join('');
}

/** Bloc « libellé + pastilles + bouton d'ajout » d'un champ RACI de la réunion.
 *
 * `withCount` ajoute le nombre de personnes à côté du libellé (demandé pour
 * Participants : le compte est l'information qu'on cherche en premier quand la
 * liste dépasse une ligne). */
export function raciBlockHtml(
  users: ReadonlyArray<ChipUser>,
  field: string,
  ids: ReadonlyArray<number>,
  label: string,
  opts: { withCount?: boolean } = {},
): string {
  const count = opts.withCount
    ? ` <span data-mp-count="${field}" style="color:var(--text3);font-weight:400">(${ids.length})</span>`
    : '';
  return `
    <div class="form-group" style="flex:1">
      <label>${esc(label)}${count}</label>
      <div class="raci-block" id="raci-${field}" data-mr-picker-zone="${field}"
        style="border:1px solid var(--border2);border-radius:var(--radius);padding:6px 8px;
               display:flex;align-items:flex-start;gap:6px;min-height:36px;cursor:pointer">
        <div class="raci-chips" id="chips-${field}"
          style="flex:1;min-height:22px;display:flex;flex-wrap:wrap;gap:2px">
          ${chipsHtml(users, field, ids)}
        </div>
        <button type="button" class="btn btn-secondary btn-icon" data-mr-picker="${field}"
          style="flex-shrink:0;font-size:14px;line-height:1;padding:3px 7px;align-self:flex-start"
          title="${esc(label)} +">+</button>
      </div>
    </div>`;
}

/** Pousse dans un champ RACI les personnes cochées du panneau de proposition.
 *
 * Silencieux sur les inconnus (utilisateur retiré entre le calcul et le clic) :
 * on alerte seulement si RIEN n'a pu être ajouté, sinon on ferait du bruit sur
 * un cas sans conséquence. */
export function addProposedUsersToField(
  users: ReadonlyArray<ChipUser>,
  field: string,
  proposed: ReadonlyArray<{ user_id?: number }>,
): void {
  if (!proposed.length) {
    toast(t('pageMeeting.noUserSelected'), 'error');
    return;
  }
  let added = 0;
  for (const u of proposed) {
    const full = u.user_id ? users.find((x) => x.id === u.user_id) : undefined;
    if (!full) continue;
    _addRaciChip(field, full);
    added += 1;
  }
  if (!added) toast(t('common.error'), 'error');
}

// ─── Mise en évidence ───────────────────────────────────────────────────────

function participantChips(): HTMLElement[] {
  const host = document.getElementById(`chips-${PARTICIPANTS_FIELD}`);
  if (!host) return [];
  return Array.from(host.querySelectorAll<HTMLElement>('.raci-chip[data-uid]'));
}

function chipUserId(chip: HTMLElement): number {
  return parseInt(chip.dataset['uid'] || '0') || 0;
}

function underline(el: HTMLElement, color: string | null): void {
  el.style.textDecoration = color ? 'underline' : '';
  el.style.textDecorationColor = color || '';
  el.style.textDecorationThickness = color ? UNDERLINE : '';
  el.style.textUnderlineOffset = color ? UNDERLINE : '';
}

/** Ajoute le score au tooltip de la pastille, sans l'empiler à chaque passe. */
function chipTitleWithScore(chip: HTMLElement, score: number | null, isMandatory = false): void {
  const base = chip.dataset['mpBaseTitle'] ?? chip.title;
  chip.dataset['mpBaseTitle'] = base;
  if (isMandatory) {
    // Sans mention, l'absence de soulignement se lit comme un oubli du calcul.
    chip.title = `${base} — ${t('pageMeeting.invitees.mandatory')}`;
    return;
  }
  chip.title = score === null ? base : `${base} — ${score} ${t('pageInviteCommittee.pointSuffix')}`;
}

/** Invités OBLIGATOIRES : pilotes du comité, et pilotes du projet/programme
 * quand le comité les convie d'office (`committee_invite_pilot`). Hors de ce
 * réglage, un pilote de projet n'est pas obligatoire — on ne l'exempte donc
 * pas du jugement de pertinence. */
function mandatoryInviteeIds(proposal: InvitationResult | null): Set<number> {
  const ids = new Set<number>();
  if (!proposal) return ids;
  const lists = [
    proposal.committee_pilots,
    proposal.scope.committee_invite_pilot ? proposal.project_program_pilots : [],
  ];
  for (const list of lists) {
    for (const u of list || []) {
      if (u.user_id) ids.add(u.user_id);
    }
  }
  return ids;
}

/** Nombre de participants, affiché à côté du libellé du champ. */
export function updateParticipantsCount(): void {
  const badge = document.querySelector<HTMLElement>(`[data-mp-count="${PARTICIPANTS_FIELD}"]`);
  if (badge) badge.textContent = `(${participantChips().length})`;
}

/** Applique (ou retire, si `proposal` est nul) la mise en évidence de pertinence.
 *
 * Idempotent : rejouée à chaque changement de la liste des participants. */
export function applyInviteeHighlights(proposal: InvitationResult | null): void {
  updateParticipantsCount();

  const scores = new Map<number, number>();
  let maxScore = 0;
  for (const u of proposal?.scored || []) {
    if (!u.user_id) continue;
    const s = u.score || 0;
    scores.set(u.user_id, s);
    maxScore = Math.max(maxScore, s);
  }

  const mandatory = mandatoryInviteeIds(proposal);

  // Participants déjà saisis : couleur du score relatif. Absent du calcul =
  // 0 pt = aucune raison identifiée d'être là. Les invités OBLIGATOIRES
  // (pilotes) échappent au jugement : leur présence ne se justifie pas par des
  // actions ou des risques portés, les souligner en rouge serait un faux
  // signal.
  const invited = new Set<number>();
  for (const chip of participantChips()) {
    const uid = chipUserId(chip);
    if (uid) invited.add(uid);
    const label = chip.querySelector<HTMLElement>('.raci-chip-label') ?? chip;
    if (!proposal || mandatory.has(uid)) {
      underline(label, null);
      chipTitleWithScore(chip, null, mandatory.has(uid) && !!proposal);
      continue;
    }
    const score = scores.get(uid) ?? 0;
    underline(label, relativeScoreColor(score, maxScore));
    chipTitleWithScore(chip, score);
  }

  // Suggérés non retenus : le trigramme est souligné de la couleur de sa
  // propre barre de score relatif — sauf barre rouge, où l'absence de la
  // personne est justement normale.
  document.querySelectorAll<HTMLElement>('[data-ip-trigram]').forEach((cell) => {
    const uid = parseInt(cell.dataset['ipUserId'] || '0') || 0;
    const color = suggestionUnderlineColor(cell.dataset['ipColor'] || '', invited.has(uid));
    underline(cell, color);
    cell.title = color
      ? t('pageMeeting.invitees.missingParticipant')
      : cell.dataset['ipName'] || cell.title;
  });
}

/** Suit les pastilles du champ Participants et rejoue la mise en évidence.
 *
 * Retourne la fonction d'arrêt (à passer à `registerPageDisposer`) : sans elle,
 * l'observateur survivrait à la navigation. */
/** Animateurs et participants tels que les pastilles les affichent. */
export interface MeetingRaciIds {
  animatorIds: number[];
  participantIds: number[];
}

export function readRaciIds(): MeetingRaciIds {
  return {
    animatorIds: _getRaciIds(ANIMATORS_FIELD),
    participantIds: _getRaciIds(PARTICIPANTS_FIELD),
  };
}

/**
 * Observe les deux champs RACI de la réunion. À chaque ajout ou retrait de
 * pastille (picker, proposition, « × »), `onChange` reçoit les listes à jour.
 *
 * Les deux champs, pas seulement les participants : sans ça, une modification
 * des animateurs faite puis quittée avec l'onglet était perdue, le markup
 * étant détruit avant l'enregistrement.
 */
export function observeRaciFields(onChange: (ids: MeetingRaciIds) => void): () => void {
  const obs = new MutationObserver(() => onChange(readRaciIds()));
  let observed = false;
  for (const field of [ANIMATORS_FIELD, PARTICIPANTS_FIELD]) {
    const host = document.getElementById(`chips-${field}`);
    if (!host) continue;
    obs.observe(host, { childList: true });
    observed = true;
  }
  return observed ? () => obs.disconnect() : () => {};
}

// ─── Câblage des champs RACI ────────────────────────────────────────────────

/**
 * Bouton « + », clic sur la zone et « × » des pastilles d'un ou plusieurs
 * champs RACI. Rappelé après chaque rendu de l'onglet Participants. Sans
 * droit d'écriture, la zone n'ouvre pas le picker (le bouton « + » reste
 * câblé : il est absent ou verrouillé par ailleurs).
 */
export function wireRaciInScope(
  scope: ParentNode,
  users: ReadonlyArray<ChipUser>,
  canWrite: boolean,
): void {
  scope.querySelectorAll<HTMLButtonElement>('[data-mr-picker]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      showRaciPicker(btn.dataset['mrPicker'] || '', btn, users);
    });
  });
  scope.querySelectorAll<HTMLElement>('[data-mr-picker-zone]').forEach((zone) => {
    zone.addEventListener('click', (e) => {
      if (!canWrite) return;
      const target = e.target as HTMLElement;
      if (target.closest('.raci-chip') || target.closest('[data-remove-raci]')) return;
      if (target.closest('[data-mr-picker]')) return;
      const btn = zone.querySelector<HTMLButtonElement>('[data-mr-picker]');
      if (btn) showRaciPicker(zone.dataset['mrPickerZone'] || '', btn, users);
    });
  });
  scope.querySelectorAll<HTMLButtonElement>('[data-remove-raci]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      _removeRaciChip(btn.dataset['field'] || '', parseInt(btn.dataset['removeRaci'] || '0'));
    });
  });
}

// ─── Proposition de participants ───────────────────────────────────────────

/**
 * « Proposer une liste de participants » : calcule la proposition pour le
 * périmètre saisi et la rend dans `#mp-proposal-host`, avec deux actions de
 * masse (ajouter aux animateurs, aux participants). Renvoie le résultat pour
 * la mise en évidence, `null` après un toast si le périmètre est incomplet
 * ou si le calcul échoue.
 */
export async function proposeParticipants(
  state: Pick<
    MeetingPageState,
    'programId' | 'projectId' | 'structureId' | 'committeeId' | 'withRisks'
  >,
  users: ReadonlyArray<ChipUser>,
): Promise<InvitationResult | null> {
  if (!state.projectId && !state.programId && !state.structureId) {
    toast(t('pageInviteCommittee.errPickProgramOrProject'), 'error');
    return null;
  }
  if (!state.committeeId) {
    toast(t('pageInviteCommittee.errPickCommittee'), 'error');
    return null;
  }
  try {
    const result = await fetchInvitationProposal({
      programId: state.programId || null,
      projectId: state.projectId || null,
      structureId: state.structureId || null,
      committeeId: state.committeeId,
      withRisks: state.withRisks,
    });
    const host = document.getElementById('mp-proposal-host');
    if (!host) return null;
    const addTo = (field: string) => (list: PilotUser[]) =>
      addProposedUsersToField(users, field, list);
    renderProposalPanel(host, result, {
      bulkActions: [
        {
          label: t('pageMeeting.btnAddAnimator'),
          title: t('pageMeeting.btnAddAnimatorTitle'),
          onClick: addTo('mp-animators'),
        },
        {
          label: t('pageMeeting.btnAddParticipant'),
          title: t('pageMeeting.btnAddParticipantTitle'),
          onClick: addTo('mp-participants'),
        },
      ],
    });
    return result;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : t('common.error');
    toast(t('pageInviteCommittee.errPrefix') + ' ' + msg, 'error');
    return null;
  }
}

/** Observation des pastilles RACI liée au cycle de vie de l'onglet. */
export interface RaciSync {
  /** (Re)démarre sur les champs actuellement montés. */
  restart: () => void;
  stop: () => void;
}

/**
 * Garde les listes RACI de `state` alignées sur les pastilles affichées.
 * L'onglet Participants détruit son markup quand on le quitte : sans cette
 * recopie, un ajout ou un retrait fait puis quitté était perdu à
 * l'enregistrement, qui relit alors l'état.
 */
export function createRaciSync(
  state: Pick<MeetingPageState, 'animatorIds' | 'participantIds'>,
  onChange: () => void,
): RaciSync {
  let dispose: (() => void) | null = null;
  const stop = (): void => {
    dispose?.();
    dispose = null;
  };
  return {
    stop,
    restart: () => {
      stop();
      dispose = observeRaciFields((ids) => {
        state.animatorIds = ids.animatorIds;
        state.participantIds = ids.participantIds;
        onChange();
      });
    },
  };
}
